import { initialDemoState } from './initialState';
class AppStore {
    constructor() {
        this.state = JSON.parse(JSON.stringify(initialDemoState));
    }
    addContribution(entityId, update) {
        const entity = this.state.mainAppState.entities[entityId];
        if (!entity) {
            console.error(`Entity with ID ${entityId} not found.`);
            return;
        }
        const newContribution = {
            id: `contrib-${new Date().getTime()}`,
            timestamp: new Date().toISOString(),
            entityId: entityId,
            actorId: this.state.user.uuid,
            value: update.value,
            unit: entity.goalUnit || 'units',
            notes: update.notes
        };
        if (!this.state.mainAppState.contributions[entityId]) {
            this.state.mainAppState.contributions[entityId] = [];
        }
        this.state.mainAppState.contributions[entityId].push(newContribution);
        entity.currentValue += update.value;
        if (entity.goalValue && entity.currentValue > entity.goalValue) {
            entity.currentValue = entity.goalValue;
        }
    }
    /**
     * Retrieves the current, immutable state of the application.
     * ARCHITECTURAL ROLE (Caching): This method serves as the primary "CACHE READ"
     * operation for performance-critical components like OnboardingComplete.
     */
    getState() {
        return this.state;
    }
    resetStateToDemoModel() {
        this.state = JSON.parse(JSON.stringify(initialDemoState));
    }
    // --- Actions to manipulate the state ---
    updateLifePriorities(priorities) {
        this.state.user.onboardingInputs.lifePriorities = priorities;
    }
    /**
     * Updates the store with the result of the holistic story analysis.
     * ARCHITECTURAL ROLE (Caching): This method serves as the primary "CACHE WRITE"
     * operation. After a successful but expensive API call, the result is stored
     * here to provide instantaneous access for subsequent component loads, preventing
     * redundant API calls.
     * @param analysis The complete analysis result from the AI engine.
     */
    updateHolisticStoryAnalysis(analysis) {
        this.state.user.holisticStoryAnalysis = analysis;
    }
    updateSevenStories(stories) {
        this.state.user.onboardingInputs.sevenStories = stories.map((s, i) => ({
            id: `story-${i + 1}`,
            narrative: s,
            analysis: { storyArchetype: "Unknown" }
        }));
    }
    updateBfiProfile(profile) {
        this.state.user.profile.bfiProfile = Object.assign(Object.assign({}, this.state.user.profile.bfiProfile), profile);
    }
    updateWorkStyle(choices) {
        this.state.user.onboardingInputs.workStyle = choices;
        const { resilience, learning, collaboration } = choices;
        if (resilience === 'push' && learning === 'dive' && collaboration === 'share') {
            this.state.user.profile.workStyleArchetype = 'Independent Driver';
        }
        else if (resilience === 'pause' && learning === 'guide' && collaboration === 'listen') {
            this.state.user.profile.workStyleArchetype = 'Collaborative Synthesizer';
        }
        else if (resilience === 'push' && learning === 'dive' && collaboration === 'listen') {
            this.state.user.profile.workStyleArchetype = 'Focused Innovator';
        }
        else {
            this.state.user.profile.workStyleArchetype = 'Adaptive Problem-Solver';
        }
    }
    updateValuesAndBelonging(data) {
        this.state.user.onboardingInputs.personalValues = data.values;
        this.state.user.onboardingInputs.belongingSense = data.belonging;
    }
    updateCoreProjects(projects) {
        this.state.user.onboardingInputs.personalProjects = projects;
        const newEntities = {};
        projects.forEach(p => {
            var _a;
            // This now constructs a complete, well-formed IImplementalEntity object,
            // conserving all necessary state properties.
            const [tempGoalValue, tempGoalUnit] = ((_a = p.measuredGoal) === null || _a === void 0 ? void 0 : _a.split(' ')) || [0, 'units'];
            let goalValue = tempGoalValue;
            let goalUnit = tempGoalUnit;
            newEntities[p.id] = {
                id: p.id,
                type: 'PROJECT',
                name: p.name,
                goalValue: goalValue,
                goalUnit: goalUnit || 'units',
                currentValue: 0
            };
        });
        // This now replaces the old entities with a new, correctly-formed set of entities.
        this.state.mainAppState.entities = newEntities;
    }
    setScheduleArchetype(archetype) {
        this.state.mainAppState.currentState.selectedScheduleArchetype = archetype;
    }
    setGeneratedSchedule(scheduleData) {
        this.state.mainAppState.generatedSchedule = scheduleData;
    }
    acceptSchedule() {
        this.state.mainAppState.currentState.selectedScheduleArchetype = null;
        this.state.mainAppState.generatedSchedule = null;
    }
}
export const appStore = new AppStore();
